<template>
  <div class="widget-wrapper">
    <template-app>
      <component-router />
    </template-app>

    <ai-notification
      :notification-active="notificationShow"
      :notification-text="notificationText"
      :notification-type="notificationType"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';

import { useEventBus } from '@vueuse/core';
import { setLocale } from '@/utils/setLocale';

import { useCommonStore } from '@/store/common/index';

import TemplateApp from '@/templates/TemplateApp/TemplateApp.vue';
import ComponentRouter from '@/components/common/ComponentRouter/ComponentRouter.vue';

import AiNotification from '@/components/UI/AiNotification/AiNotification.vue';

const { params, isMobile, currentService, servicesList } = storeToRefs(useCommonStore());

const resizeObserver = ref<ResizeObserver | null>(null);
const notificationShow = ref<boolean>(false);
const notificationText = ref<string>('');
const notificationType = ref<'success' | 'pending' | 'error'>('error');

const handleResize = (): void => {
  const result = document.querySelector((window as any).AI_WIDGET_SETTINGS.el).offsetWidth <= 767;
  isMobile.value = result;
};

const busError = useEventBus<string>('toast-error');
const busSuccess = useEventBus<string>('toast-success');

busError.on((_message) => {
  showToast({
    type: 'error',
    message: _message,
  });
});

busSuccess.on((_message) => {
  showToast({
    type: 'success',
    message: _message,
  });
});

const showToast = ({ type, message }: { type: 'success' | 'pending' | 'error'; message: string }): void => {
  notificationShow.value = true;
  notificationText.value = message;
  notificationType.value = type;
  setTimeout(() => {
    notificationShow.value = false;
    notificationText.value = '';
  }, 5000);
};

onBeforeMount(() => {
  params.value = (window as any)?.AI_WIDGET_SETTINGS || null;
  if ((window as any)?.AI_WIDGET_SETTINGS?.currentService) {
    currentService.value = (window as any)?.AI_WIDGET_SETTINGS?.currentService;
  }
  if ((window as any)?.AI_WIDGET_SETTINGS?.servicesList) {
    servicesList.value = (window as any)?.AI_WIDGET_SETTINGS?.servicesList;
  }

  if ((window as any)?.AI_WIDGET_SETTINGS?.lang) {
    setLocale((window as any)?.AI_WIDGET_SETTINGS?.lang);
  }
});

onMounted(() => {
  resizeObserver.value = new ResizeObserver(handleResize);
  resizeObserver.value.observe(document.querySelector((window as any).AI_WIDGET_SETTINGS.el));
  handleResize();
});

onBeforeUnmount(() => {
  if (resizeObserver.value) {
    resizeObserver.value.unobserve(document.querySelector((window as any).AI_WIDGET_SETTINGS.el));
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
  }
});
</script>
